import { InternalDatabaseService } from '@stockcare-storage/internal/internal-database.service';
import { Injectable } from '@angular/core';
import { QueueItem } from './queue';
import { Observable } from 'rxjs';
import { QueueStatus } from '@stockcare-models/QueueStatus';
import { QueueType } from '@stockcare-models/QueueType';

@Injectable({
  providedIn: 'root',
})
export class QueueStorageService {
  readonly table: string;
  readonly index: string;

  constructor(private internalDatabaseService: InternalDatabaseService) {
    const table = QueueItem.getTableDefinition();
    this.table = table.TableName;
    this.index = table.Indexes[0].Name;
  }

  enqueue(queueItem: QueueItem): Observable<boolean> {
    return new Observable((observer) => {
      this.internalDatabaseService
        .add<QueueItem>(this.table, queueItem)
        .then(() => {
          observer.next(true);
        })
        .catch((error) => {
          observer.error(false);
        });
    });
  }

  dequeue(): Observable<QueueItem> {
    return new Observable((observer) => {
      this.internalDatabaseService
        .getAll<QueueItem>(this.table)
        .then((items) => {
          if (!items) {
            observer.next(null);
            return;
          }

          items = items.filter((x) => x.Status === QueueStatus.New);

          if (!items || items.length === 0) {
            observer.next(null);
            return;
          }

          const sortedItems = this.sortByRetryCount(items);

          const dequeuedItem = sortedItems[0];
          dequeuedItem.Status = QueueStatus.InProgress;
          dequeuedItem.DateModified = new Date();
          dequeuedItem.RetryCount++;

          this.update(dequeuedItem).subscribe(
            () => {
              observer.next(dequeuedItem);
            },
            () => {
              observer.error(false);
            }
          );
        })
        .catch((error) => {
          observer.error(false);
        });
    });
  }

  private sortByRetryCount(items: QueueItem[]) {
    return items.sort((item1, item2) => {
      return item2.RetryCount - item1.RetryCount;
    });
  }

  retry(id: number, error: any) {
    return new Observable((observer) => {
      this.internalDatabaseService.getByPrimaryKey(this.table, id).then((queueItem: QueueItem) => {
        queueItem.DateModified = new Date();
        queueItem.Status = QueueStatus.New;
        queueItem.ServerResponse = JSON.stringify(error);

        this.internalDatabaseService
          .update(this.table, queueItem)
          .then(() => {
            observer.next();
          })
          .catch(() => observer.error());
      });
    });
  }

  complete(id: number) {
    return new Observable((observer) => {
      this.internalDatabaseService.getByPrimaryKey(this.table, id).then((queueItem: QueueItem) => {
        queueItem.DateModified = new Date();
        queueItem.Status = QueueStatus.Complete;

        this.internalDatabaseService
          .update(this.table, queueItem)
          .then(() => {
            observer.next();
          })
          .catch(() => observer.error());
      });
    });
  }

  ensureOnlyOneItemPerTheatreQueueType(caseNumber: string, queueType: QueueType) {
    return new Observable((observer) => {
      this.internalDatabaseService
        .getAll<QueueItem>(this.table)
        .then((items) => {
          if (!items) {
            observer.next(null);
            return;
          }

          items = items.filter((x) => x.Type === queueType);
          items = items.filter((x => x.Value.includes(caseNumber)));

          if (!items || items.length === 0) {
            observer.next(null);
            return;
          }

         items.forEach(itemToDelete => {
          this.internalDatabaseService.delete(this.table, itemToDelete.Id);
         });

         observer.next();
        })
        .catch((error) => {});
    });
  }

  update(queueItem): Observable<boolean> {
    return new Observable((observer) => {
      this.internalDatabaseService
        .update(this.table, queueItem)
        .then(() => observer.next(true))
        .catch(() => observer.error(false));
    });
  }

  getPendingCount(): Observable<number> {
    return new Observable((observer) => {
      this.internalDatabaseService
        .getAll<QueueItem>(this.table)
        .then((result) => observer.next(result.length))
        .catch(() => observer.error(99));
    });
  }
}
